import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext(null);
const STORAGE_KEY = 'awp_theme';

const isAdminPath = (pathname) => pathname.startsWith('/dashboard/admin');

const getInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        user: parsed.user === 'dark' ? 'dark' : 'light',
        admin: parsed.admin === 'dark' ? 'dark' : 'light',
      };
    }
  } catch (_) {}
  return { user: 'light', admin: 'light' };
};

export const ThemeProvider = ({ children }) => {
  const [themes, setThemes] = useState(getInitial);
  const location = useLocation();
  const scope = useMemo(() => (isAdminPath(location.pathname) ? 'admin' : 'user'), [location.pathname]);
  const activeTheme = themes[scope];

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activeTheme);
    document.documentElement.setAttribute('data-scope', scope);
  }, [activeTheme, scope]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
    } catch (_) {}
  }, [themes]);

  const toggleTheme = useCallback((targetScope) => {
    const s = targetScope || scope;
    setThemes((prev) => ({ ...prev, [s]: prev[s] === 'dark' ? 'light' : 'dark' }));
  }, [scope]);

  const setTheme = useCallback((targetScope, value) => {
    setThemes((prev) => ({ ...prev, [targetScope]: value }));
  }, []);

  return (
    <ThemeContext.Provider value={{
      theme: activeTheme,
      scope,
      userTheme: themes.user,
      adminTheme: themes.admin,
      toggleTheme,
      setTheme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export { isAdminPath };
